import { DealPipelineInvestmentsQuery } from '@/__generated__/graphql';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { DeleteBucketIcon } from '@/assets/static/icons/delete-bucket';
import { Banner } from '@/components/basicComponents/banner';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import { useErrorMessages } from '@/hooks/i18n';
import { themeVar } from '@/styles/theme';
import { useMutation } from '@apollo/client';
import { useState } from 'react';
import styled from 'styled-components';
import { DEAL_PIPELINE_INVESTMENTS_QUERY, DEAL_PIPELINE_PAGE_EXECUTE_WORKFLOW_TRANSITION_MUTATION } from '../queries';

interface DeleteInvestmentProps {
  isOpen: boolean;
  currentRow: DealPipelineInvestmentsQuery['Investments']['data'][number] | null;
  onClose: () => void;
}

const DeleteInvestment = ({ isOpen, currentRow, onClose }: DeleteInvestmentProps) => {
  const { default: defaultErrorMessage } = useErrorMessages();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [executeWorkflowTransition, { loading: executeWorkflowTransitionLoading }] = useMutation(
    DEAL_PIPELINE_PAGE_EXECUTE_WORKFLOW_TRANSITION_MUTATION,
    {
      awaitRefetchQueries: true,
      refetchQueries: [DEAL_PIPELINE_INVESTMENTS_QUERY]
    }
  );

  const saveValue = async () => {
    try {
      setErrorMessage(null);

      if (!currentRow) {
        setErrorMessage(defaultErrorMessage);
        return;
      }

      const workflowTransitionIndex = currentRow.availableWorkflowTransitions.findIndex((transition) => transition.name === 'deleteInvestment');

      if (workflowTransitionIndex === -1) {
        setErrorMessage(defaultErrorMessage);
        return;
      }

      await executeWorkflowTransition({
        variables: {
          data: {
            instanceId: currentRow.workflowInstance.id,
            transitionId: currentRow.availableWorkflowTransitions[workflowTransitionIndex].id
          }
        }
      });

      onClose();
    } catch (error) {
      console.error(error);
      setErrorMessage(defaultErrorMessage);
    }
  };

  if (!currentRow) return <></>;

  return (
    <ModalWrappedContent
      isOpen={isOpen}
      onClose={onClose}
      confirmButton={{
        name: 'Yes, Delete',
        onClick: saveValue,
        disabled: executeWorkflowTransitionLoading
      }}
      showCancelButton={!executeWorkflowTransitionLoading}
      showCloseIcon={!executeWorkflowTransitionLoading}
      isUseOutsideClick={!executeWorkflowTransitionLoading}
    >
      <ModalContainer>
        <DeleteBucketIcon fill={themeVar('font.disabled')} />
        {executeWorkflowTransitionLoading ? (
          <ModalLoader />
        ) : (
          <>
            <ModalTitel>Please confirm you want to delete this strategy.</ModalTitel>
            {errorMessage && (
              <ErrorBannerWrapper>
                <Banner
                  icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
                  title="Error"
                  description={errorMessage}
                  bgColor={themeVar('context.error')}
                />
              </ErrorBannerWrapper>
            )}
            <ModalDescription>This strategy will be permanently deleted. This action cannot be undone.</ModalDescription>
          </>
        )}
      </ModalContainer>
    </ModalWrappedContent>
  );
};

export default DeleteInvestment;

const ModalContainer = styled.div`
  font-weight: 400;
  padding: 10px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
`;

const ModalTitel = styled.div`
  font-size: 33px;
  text-align: center;
  color: ${themeVar('font.strong')};
`;

const ModalDescription = styled.div`
  font-size: 16px;
  line-height: 27px;
  text-align: center;
  color: ${themeVar('font.base')};
`;

const ErrorBannerWrapper = styled.div`
  margin-bottom: 10px;
  width: 100%;
`;
